import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthPage } from '../../hooks/useAuthPage';
import { isTinycmsFunnel } from './tinycmsPlans';
import { TinycmsSignupFlow } from './TinycmsSignupFlow';

import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { ArrowLeft, Check } from 'lucide-react';

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  popular?: boolean;
}

const pricingPlans: PricingPlan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 9999,
    description: 'Perfect for individuals',
    features: [
      'Up to 2 social profiles',
      'Up to 100 posts per month',
      'Basic analytics',
      'Email support',
      '14-day free trial',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 9999,
    description: 'For professionals and small teams',
    features: [
      'Unlimited 10 social profiles',
      'Up to 1000 posts per month',
      'Advanced analytics',
      'Priority support',
      'Team collaboration',
    ],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 9999,
    description: 'For large organizations',
    features: [
      'Everything in Pro',
      'Unlimited social profiles',
      'Unlimited posts per month',
      'Manage multiple organizations',
      'SSO integration',
      'Custom 3rd party integrations',
      'Dedicated account manager',
      'SLA guarantee',
    ],
  },
];

const validPlanIds = pricingPlans.map((plan) => plan.id);

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  plan?: string;
}

/** TinyCMS hosts sell the TinyCMS plans; every other host keeps its own funnel. */
export function SignUp() {
  return isTinycmsFunnel() ? <TinycmsSignupFlow /> : <LegacySignUp />;
}

function LegacySignUp() {
  useAuthPage(); // Enable natural page scrolling
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const planFromUrl = searchParams.get('plan');
  const initialPlan = planFromUrl && validPlanIds.includes(planFromUrl) ? planFromUrl : '';

  const [selectedPlan, setSelectedPlan] = useState<string>(initialPlan);
  const [errors, setErrors] = useState<FormErrors>({});
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  // Sync from the URL only when a valid ?plan= param appears; never clear a
  // selection the user made by clicking a plan card.
  useEffect(() => {
    if (planFromUrl && validPlanIds.includes(planFromUrl)) {
      setSelectedPlan(planFromUrl);
    }
  }, [planFromUrl]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!selectedPlan) {
      newErrors.plan = 'Please select a plan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onBack = () => {
    navigate('/login');
  };

  // Signup mutation will be handled in PaymentPage after payment confirmation

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form first
    if (!validateForm()) {
      return;
    }

    // Navigate to payment page with signup data
    navigate('/payment', {
      state: {
        email,
        password,
        selectedPlan,
      },
    });
  };
  return (
    <div className="min-h-screen px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="mb-8">
          <h2 className="mb-2">Create Your Account</h2>
          <p className="text-muted-foreground">
            Choose your plan and enter your details to get started
          </p>
        </div>

        <form onSubmit={handleSubmit} className="pb-8">
          {/* Email and Password */}
          <Card className="mb-7 border-0 bg-core-dark p-2">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Enter your email and create a password</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.email && <p className="text-destructive">{errors.email}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.password && <p className="text-destructive">{errors.password}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  className="w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {errors.confirmPassword && (
                  <p className="text-destructive">{errors.confirmPassword}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Plan Selection */}
          <div className="mb-7">
            <Label className="mb-4 block text-lg">Select Your Plan</Label>
            <RadioGroup value={selectedPlan} onValueChange={(value) => setSelectedPlan(value)}>
              {errors.plan && <p className="text-destructive mb-2">{errors.plan}</p>}
              <div className="grid md:grid-cols-3 gap-6">
                {pricingPlans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`relative cursor-pointer rounded-lg border transition-all ${
                      selectedPlan === plan.id
                        ? 'border-primary ring-2 ring-primary ring-offset-2'
                        : 'hover:border-primary/50'
                    }`}
                  >
                    <Card className="border-0 p-2 bg-core-dark h-full">
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <RadioGroupItem value={plan.id} id={plan.id} />
                          {plan.popular && (
                            <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full">
                              Most Popular
                            </span>
                          )}
                        </div>
                        <CardTitle>{plan.name}</CardTitle>
                        <div className="mt-4">
                          <span className="font-bold">${plan.price}</span>
                          <span className="text-muted-foreground">/month</span>
                        </div>
                        <CardDescription>{plan.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>

          <Button type="submit" className="w-full mb-8">
            Continue to Payment
          </Button>
        </form>
      </div>
    </div>
  );
}
